import { useState, useCallback } from 'react';
import supabase from '@/lib/supabase';

interface UploadedImage {
  url: string;
  path: string;
  uploading: boolean;
}

export function useImageUpload() {
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [uploadError, setUploadError] = useState('');

  const uploadImage = useCallback(async (file: File): Promise<string | null> => {
    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 10)}.${fileExt}`;
    const filePath = `posts/${fileName}`;

    const tempImage: UploadedImage = { url: '', path: filePath, uploading: true };
    setImages((prev) => [...prev, tempImage]);
    setUploadError('');

    try {
      const { error } = await supabase.storage
        .from('post-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        if (error.message?.includes('bucket') || error.message?.includes('not found')) {
          throw new Error('Storage 버킷이 설정되지 않았습니다. Supabase 대시보드에서 "post-images" 버킷을 생성해주세요.');
        }
        throw error;
      }

      const { data: urlData } = supabase.storage
        .from('post-images')
        .getPublicUrl(filePath);

      const publicUrl = urlData?.publicUrl || '';

      setImages((prev) =>
        prev.map((img) =>
          img.path === filePath ? { ...img, url: publicUrl, uploading: false } : img
        )
      );

      return publicUrl;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '이미지 업로드 중 오류가 발생했습니다.';
      setUploadError(message);
      setImages((prev) => prev.filter((img) => img.path !== filePath));
      return null;
    }
  }, []);

  const removeImage = useCallback((path: string) => {
    setImages((prev) => prev.filter((img) => img.path !== path));
    supabase.storage
      .from('post-images')
      .remove([path])
      .then(() => {})
      .catch(() => {});
  }, []);

  const setInitialImages = useCallback((urls: string[]) => {
    const initial = urls
      .filter((url) => url && url.trim())
      .map((url, i) => ({
        url,
        path: `existing-${i}`,
        uploading: false,
      }));
    setImages(initial);
  }, []);

  const getImageUrls = useCallback((): string[] => {
    return images.filter((img) => !img.uploading && img.url).map((img) => img.url);
  }, [images]);

  return {
    images,
    uploadError,
    uploadImage,
    removeImage,
    setInitialImages,
    getImageUrls,
    setUploadError,
  };
}